import { Yup } from '@/src/components/legacy/forms/src'

// QUANTITY_ADJUSTMENT (mengenanpassung) can be both percentage quantity and absolute quantity.
export enum LoadAdjustmentFormTypeEnum {
  REFERENCE_PERIOD = 'REFERENCE_PERIOD',
  COMPANY_HOLIDAYS = 'COMPANY_HOLIDAYS',
  QUANTITY_ADJUSTMENT = 'QUANTITY_ADJUSTMENT',
  RENEWABLE_PRODUCTION = 'RENEWABLE_PRODUCTION',
}

export type QuantityAdjustmentType = 'percentage' | 'absolute'

export const RENEWABLE_SOURCE_PROFILES = ['SOLAR', 'WIND_ONSHORE', 'WIND_OFFSHORE'] as const

export type RenewableSourceProfile = (typeof RENEWABLE_SOURCE_PROFILES)[number]

export const ANNUAL_PRODUCTION_UNITS = ['kWh', 'MWh', 'GWh'] as const

export type AnnualProductionUnit = (typeof ANNUAL_PRODUCTION_UNITS)[number]

const optionalNumber = Yup.number()
  .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
  .optional()

export const formValidationSchema = Yup.object({
  id: Yup.string().optional(),
  locationId: Yup.number().required('Location is required'),
  name: Yup.string().required('Name is required'),
  type: Yup.string().required('Type is required'),
  timeframeStart: Yup.string().required('Start date is required'),
  timeframeEnd: Yup.string().required('End date is required'),
  comment: Yup.string().optional(),
  // Company Holidays specific
  baseLoad: Yup.number().when('type', {
    is: LoadAdjustmentFormTypeEnum.COMPANY_HOLIDAYS,
    then: schema => schema.required('Baseload is required'),
    otherwise: schema => schema.optional(),
  }),
  // Quantity Adjustments specific (both absolute and percentage)
  adjustmentType: Yup.string().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT,
    then: schema => schema.oneOf(['percentage', 'absolute']).optional(),
    otherwise: schema => schema.optional(),
  }),
  adjustmentValue: Yup.number().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT,
    then: () => Yup.number().required('Adjustment value is required'),
    otherwise: () => Yup.number().optional(),
  }),
  adjustmentValueUnit: Yup.string().when(['type', 'adjustmentType'], {
    is: (type: string, adjustmentType: string) => type === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT && adjustmentType === 'absolute',
    then: () => Yup.string().oneOf(['kW', 'kWh']).required('Unit is required'),
    otherwise: () => Yup.string().optional(),
  }),
  applicableWeekdays: Yup.array()
    .of(Yup.string().oneOf(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']).required())
    .when('type', {
      is: (value: string) => value === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT,
      then: schema => schema.optional(),
      otherwise: schema => schema.optional(),
    }),
  timeFrom: Yup.string().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT,
    then: schema => schema.optional().nullable(),
    otherwise: schema => schema.optional().nullable(),
  }),
  timeTo: Yup.string().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT,
    then: schema => schema.optional().nullable(),
    otherwise: schema => schema.optional().nullable(),
  }),
  allDay: Yup.boolean().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT,
    then: schema => schema.optional(),
    otherwise: schema => schema.optional(),
  }),
  // TODO - WIBI: reference period start - end MUST BE AT LEAST 7 DAYS
  referencePeriodStart: Yup.string().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.REFERENCE_PERIOD,
    then: schema => schema.required(),
    otherwise: schema => schema.optional(),
  }),
  referencePeriodEnd: Yup.string().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.REFERENCE_PERIOD,
    then: schema => schema.required(),
    otherwise: schema => schema.optional(),
  }),
  percentageAdjustmentValue: Yup.number().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.REFERENCE_PERIOD,
    then: () => optionalNumber,
    otherwise: schema => schema.optional(),
  }),
  // Renewable production specific
  totalAnnualProduction: Yup.number().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.RENEWABLE_PRODUCTION,
    then: () => Yup.number().min(0).required('Annual production is required'),
    otherwise: () => Yup.number().optional(),
  }),
  totalAnnualProductionUnit: Yup.string().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.RENEWABLE_PRODUCTION,
    then: () =>
      Yup.string()
        .oneOf([...ANNUAL_PRODUCTION_UNITS])
        .required('Unit is required'),
    otherwise: () => Yup.string().optional(),
  }),
  sourceProfile: Yup.string().when('type', {
    is: (value: string) => value === LoadAdjustmentFormTypeEnum.RENEWABLE_PRODUCTION,
    then: () =>
      Yup.string()
        .oneOf([...RENEWABLE_SOURCE_PROFILES])
        .required('Generation profile is required'),
    otherwise: () => Yup.string().optional(),
  }),
})

// DTO (if exists) -> Form type (emptyable) -> Form type (validated) -> DTO of the certain type

// Infer the form data type from the Yup validation schema
// This type is used only for initializing the form.
export type LoadPlanningAdjustmentFormEmptyable = Partial<Yup.InferType<typeof formValidationSchema>>

export type LoadPlanningAdjustmentFormValidated = Yup.InferType<typeof formValidationSchema>

export const uppercaseWeekdays = (
  weekdays: string[] | undefined
): ('MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY')[] => {
  return (weekdays?.map(day => day.toUpperCase()) ?? []) as (
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY'
  )[]
}
